import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  qDeliveryOrderLinesByOrder,
  type DeliveryOrderLineRow,
  type DeliveryOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { NavIcon } from '../../shell/shared';
import { SendEmailComingSoonModal } from './SendEmailComingSoonModal';

function formatNumber(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US');
}

function statusBadgeVariant(status: string | null): 'warning' | 'neutral' {
  return status === 'draft' ? 'warning' : 'neutral';
}

function statusLabel(status: string | null): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function DeliveryOrderDetailsDrawer({
  open,
  onOpenChange,
  deliveryOrder,
  brandTokens,
  onViewPo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryOrder: DeliveryOrderRow | null;
  brandTokens: Record<string, string>;
  /** Optional callback to open the linked PO's details (stacked drawer). */
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  const navigate = useNavigate();

  const { data: lines = [] } = usePowerSyncQuery<DeliveryOrderLineRow>(
    qDeliveryOrderLinesByOrder(deliveryOrder?.id ?? ''),
  );

  const isDraft = deliveryOrder?.status === 'draft';
  const canContinue = isDraft && deliveryOrder?.purchase_order_id != null;

  // Send Email is a placeholder action — opens a "coming soon" modal
  // so users discover the planned capability. Wired up in a future PRD.
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleContinue = () => {
    if (!deliveryOrder?.purchase_order_id) return;
    onOpenChange(false);
    navigate(`/inventory/purchase-orders/${deliveryOrder.purchase_order_id}/receive`);
  };

  const title = deliveryOrder?.do_number ?? 'Delivery Order';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      width="min(720px, 60vw)"
      badges={
        deliveryOrder ? (
          <DrawerBadge
            label={statusLabel(deliveryOrder.status)}
            variant={statusBadgeVariant(deliveryOrder.status)}
          />
        ) : null
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <IconButton
              ariaLabel="Send DO via email"
              title="Send via email"
              onClick={() => setEmailModalOpen(true)}
              disabled={!deliveryOrder}
            >
              <NavIcon name="mail" />
            </IconButton>
          </div>
          {canContinue && (
            <Button variant="gradient" size="lg" onClick={handleContinue}>
              Continue Receiving
            </Button>
          )}
        </div>
      }
    >
      <SendEmailComingSoonModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        documentLabel="Delivery Order"
        documentNumber={deliveryOrder?.do_number ?? null}
        portalStyle={brandTokens as CSSProperties}
      />
      {!deliveryOrder ? (
        <div
          style={{
            padding: 'var(--sp-6)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-6)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          <HeaderInfo deliveryOrder={deliveryOrder} onViewPo={onViewPo} />
          <LinesSection lines={lines} amountsAre={deliveryOrder.amounts_are} />
          <TotalsSection
            subtotal={deliveryOrder.subtotal}
            invoiceTotal={deliveryOrder.invoice_total}
            amountsAre={deliveryOrder.amounts_are}
          />
        </div>
      )}
    </Drawer>
  );
}

function IconButton({
  ariaLabel,
  title,
  onClick,
  disabled = false,
  children,
}: {
  ariaLabel: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: disabled ? 'var(--text-muted)' : 'var(--text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function HeaderInfo({
  deliveryOrder,
  onViewPo,
}: {
  deliveryOrder: DeliveryOrderRow;
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <Field label="Supplier" value={deliveryOrder.supplier_name ?? '—'} />
      <Field label="Supplier Invoice #" value={deliveryOrder.supplier_invoice_number ?? '—'} />
      <Field label="Reference" value={deliveryOrder.reference ?? '—'} />
      <Field label="Date Raised" value={deliveryOrder.date_raised ?? '—'} />
      <Field label="Delivery Date" value={deliveryOrder.delivery_date ?? '—'} />
      <Field label="Received Date" value={deliveryOrder.received_date ?? '—'} />
      <Field
        label="Amounts"
        value={deliveryOrder.amounts_are === 'tax_inclusive' ? 'Tax inclusive' : 'Tax exclusive'}
      />
      <LinkedPoField purchaseOrderId={deliveryOrder.purchase_order_id} onViewPo={onViewPo} />
      {deliveryOrder.notes && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes" value={deliveryOrder.notes} multiline />
        </div>
      )}
    </div>
  );
}

function LinkedPoField({
  purchaseOrderId,
  onViewPo,
}: {
  purchaseOrderId: string | null;
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  if (!purchaseOrderId) {
    return <Field label="Linked PO" value="—" />;
  }
  if (!onViewPo) {
    return <Field label="Linked PO" value="Yes" />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        Linked PO
      </span>
      <button
        type="button"
        onClick={() => onViewPo(purchaseOrderId)}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 28,
          padding: '0 10px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-primary)',
          background: 'transparent',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
        }}
      >
        View PO
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>
          →
        </span>
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
      }}
    >
      {children}
    </span>
  );
}

const LINE_COLUMNS = '1.6fr 60px 70px 70px 90px 100px';

function LinesSection({
  lines,
  amountsAre,
}: {
  lines: DeliveryOrderLineRow[];
  amountsAre: string | null;
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <SectionLabel>
        Line Items{' '}
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-muted)' }}>
          ({lines.length}){amountsAre === 'tax_inclusive' ? ' · Tax inclusive' : ''}
        </span>
      </SectionLabel>

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: LINE_COLUMNS,
            gap: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-surface-2)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
          }}
        >
          <span>Item</span>
          <span style={{ textAlign: 'right' }}>Ord</span>
          <span style={{ textAlign: 'right' }}>Recv</span>
          <span style={{ textAlign: 'right' }}>Bonus</span>
          <span style={{ textAlign: 'right' }}>Rate</span>
          <span style={{ textAlign: 'right' }}>Amount</span>
        </div>

        {lines.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4) var(--sp-3)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
            }}
          >
            No line items.
          </div>
        ) : (
          lines.map((l) => <LineRow key={l.id} line={l} />)
        )}
      </div>
    </section>
  );
}

function LineRow({ line }: { line: DeliveryOrderLineRow }) {
  const fmt = useFormatMoney();
  const batchInfo = [line.batch_number_snapshot, line.expiry_date_snapshot]
    .filter(Boolean)
    .join(' · exp ');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: LINE_COLUMNS,
        gap: 'var(--sp-3)',
        alignItems: 'flex-start',
        padding: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            fontWeight: 'var(--font-weight-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {line.item_name_snapshot ?? '—'}
        </span>
        {(line.sku_snapshot || line.unit_snapshot) && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {[line.sku_snapshot, line.unit_snapshot].filter(Boolean).join(' · ')}
          </span>
        )}
        {batchInfo && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {batchInfo}
          </span>
        )}
        {line.remarks && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {line.remarks}
          </span>
        )}
      </div>
      <span style={numCellStyle}>{formatNumber(line.quantity_ordered)}</span>
      <span style={numCellStyle}>{formatNumber(line.quantity_received)}</span>
      <span style={numCellStyle}>{formatNumber(line.bonus_quantity_received)}</span>
      <span style={numCellStyle}>{line.rate == null ? '—' : fmt(line.rate)}</span>
      <span style={{ ...numCellStyle, fontWeight: 'var(--font-weight-semi)' }}>
        {line.amount == null ? '—' : fmt(line.amount)}
      </span>
    </div>
  );
}

const numCellStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

function TotalsSection({
  subtotal,
  invoiceTotal,
  amountsAre,
}: {
  subtotal: number | null;
  invoiceTotal: number | null;
  amountsAre: string | null;
}) {
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <TotalRow label="Subtotal" value={subtotal} muted />
      <TotalRow
        label={`Invoice Total ${amountsAre === 'tax_inclusive' ? '(incl. tax)' : '(excl. tax)'}`}
        value={invoiceTotal}
      />
    </section>
  );
}

function TotalRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: number | null;
  muted?: boolean;
}) {
  const fmt = useFormatMoney();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: muted ? 'var(--font-size-sm)' : 'var(--font-size-lg)',
          fontWeight: muted ? 'var(--font-weight-medium)' : 'var(--font-weight-semi)',
          color: muted ? 'var(--text-muted)' : 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value == null ? '—' : fmt(value)}
      </span>
    </div>
  );
}
